/**
 *
 * @module entry
 * @description
 *
 */

import Check from 'check-types';
import SDK from 'bam-browser-sdk';
import { EventEmitter } from 'events';
import GeoProviderMock from './geoProviderMock';

const SdkSession = SDK.SdkSession;
const BootstrapConfiguration = SDK.Services.Configuration.BootstrapConfiguration;

/**
 *
 * @access private
 * @type {EventEmitter}
 *
 */
const eventEmitter = new EventEmitter();

/**
 *
 * @access private
 * @type {SDK.Services.Configuration.BootstrapConfiguration}
 *
 */
let bootstrapConfiguration;

/**
 *
 * @access private
 * @type {Promise}
 *
 */
let sdkSessionPromise;

/**
 *
 * @access private
 * @type {SDK.Token.GeoProvider}
 *
 */
let geoProvider;

/**
 *
 * @class SdkSessionUtil
 * @description Singleton model to get a shared instance of a SDK.SdkSession
 *
 */
class SdkSessionUtil {

    /**
     *
     * @access public
     * SdkSessionUtil.getSdkSession()
     * @param {Object} config
     * @desc Set up the bootstrap configuration for the SDK. Must be called before
     *
     */
    static setBootstrapConfiguration(config) {

        const { mockGeoLocation } = config;

        if (mockGeoLocation) {

            const { latitude, longitude } = mockGeoLocation;

            geoProvider = new GeoProviderMock(latitude, longitude);

        }

        /** Set up bootstrap configuration */
        bootstrapConfiguration = new BootstrapConfiguration(config);

    }

    /**
     *
     * @access public
     * @returns {Promise<SDK.SdkSession>}
     *
     */
    static getSdkSession() {

        /** Resolve the previously created sdkSession, if exists */
        if (Check.assigned(sdkSessionPromise)) {

            return sdkSessionPromise;

        }

        const method = 'SdkSessionUtil.getSdkSession()';
        const bootstrapConfigurationError = `${method} needs BootstrapConfiguration instance.
            Call SdkSessionUtil.setBootstrapConfiguration(config) first.`;

        Check.assert.instanceStrict(
            bootstrapConfiguration,
            BootstrapConfiguration,
            bootstrapConfigurationError
        );

        let sdkSession;

        /** Create a SDK.SdkSession and initialize it */
        sdkSessionPromise = SdkSession.createSdkSession(bootstrapConfiguration, geoProvider).then((session) => {

            sdkSession = session;

            return sdkSession.initialize().catch(() => {

                return sdkSession.reset().then(() => {

                    eventEmitter.emit('SdkSessionResetOnError');

                    return Promise.resolve();

                });

            });

        }).then(() => {

            return Promise.resolve(sdkSession);

        });

        return sdkSessionPromise;

    }

    /**
     *
     * @access public
     * @param {String} event
     * @param {Function} handler
     *
     */
    static on(event, handler) {

        eventEmitter.on(event, handler);

    }

    /**
     *
     * @access public
     * @param {String} [event]
     * @param {Function} [handler]
     * @desc If called with no parameters, it will remove all listeners. Passing only event, it will remove
     * all listeners for that event.
     *
     */
    static off(event, handler) {

        if (Check.not.assigned(handler)) {
            eventEmitter.removeAllListeners(event);
        }

        eventEmitter.removeListener(event, handler);

    }

}

/** Unique key for referencing the SdkSessionUtil in the global scope */
const SDK_SESSION_UTIL_KEY = Symbol.for('BAM.SDK.SdkSessionUtil');

/** npm:window-or-global */
const scope = (typeof self === 'object' && self.self === self && self) ||
    (typeof global === 'object' && global.global === global && global) ||
    window;

/** Get the scope's symbols */
const scopeSymbols = Object.getOwnPropertySymbols(scope);

/** Check the scope for existing symbol  */
if (scopeSymbols.indexOf(SDK_SESSION_UTIL_KEY) === -1) {

    scope[SDK_SESSION_UTIL_KEY] = SdkSessionUtil;

}

/** Singleton API */
class API {

    /**
     *
     * @returns {SdkSessionUtil}
     *
     */
    static get instance() {

        return scope[SDK_SESSION_UTIL_KEY];

    }

}

/** Lock the API */
Object.freeze(API);

/** Export the singleton */
export default API.instance;



// WEBPACK FOOTER //
// ./src/entry.js